"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ADMIN_API_URL } from "../../../admin-api/internal";
import { outboxStatsSchema } from "../../../schemas/outbox";
import { streamSse } from "../../../sse";
import { queryKeys } from "../../query-keys";

export type OutboxStreamStatus = "connecting" | "live" | "reconnecting";

const RECONNECT_DELAY_MS = 3000;

/**
 * Tail `GET /admin/outbox/stream`: `stats` frames replace the cached stats,
 * `events` frames invalidate the list so the table refetches its current page.
 */
export function useOutboxStream(): OutboxStreamStatus {
	const queryClient = useQueryClient();
	const [status, setStatus] = useState<OutboxStreamStatus>("connecting");

	useEffect(() => {
		const controller = new AbortController();
		let timer: ReturnType<typeof setTimeout> | undefined;

		const run = async () => {
			try {
				await streamSse(
					`${ADMIN_API_URL}/admin/outbox/stream`,
					(m) => {
						setStatus("live");
						switch (m.event) {
							case "stats":
								queryClient.setQueryData(
									queryKeys.outbox.stats,
									outboxStatsSchema.parse(JSON.parse(m.data)),
								);
								return;
							case "events":
								queryClient.invalidateQueries({ queryKey: queryKeys.outbox.all });
								return;
							default:
								return;
						}
					},
					controller.signal,
				);
			} catch {
				// fall through to reconnect
			}
			if (controller.signal.aborted) return;
			setStatus("reconnecting");
			timer = setTimeout(run, RECONNECT_DELAY_MS);
		};
		void run();

		return () => {
			controller.abort();
			clearTimeout(timer);
		};
	}, [queryClient]);

	return status;
}
